import { useState, useEffect } from "react";
import {
  GraduationCap,
  Plus,
  Calendar,
  MapPin,
  Users,
  Clock,
  BookOpen,
  Trophy,
  TrendingUp,
  Filter,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  Grid3X3,
  List,
  BarChart3,
  Star,
  PlayCircle,
  Award,
  Target,
  Zap,
  ArrowRight,
  ChevronDown,
  Download,
  Share2,
  Bell,
  Settings,
  Calendar as CalendarIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { trainingAPI } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import TrainingForm from "./TrainingForm";
import TrainingDetails from "./TrainingDetails";
import EnrollmentModal from "./EnrollmentModal";

const ViewToggle = ({ currentView, onViewChange }) => (
  <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
    <button
      onClick={() => onViewChange("grid")}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
        currentView === "grid"
          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      }`}
    >
      <Grid3X3 className="h-4 w-4" />
      <span className="hidden sm:inline">Grille</span>
    </button>
    <button
      onClick={() => onViewChange("list")}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
        currentView === "list"
          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      }`}
    >
      <List className="h-4 w-4" />
      <span className="hidden sm:inline">Liste</span>
    </button>
    <button
      onClick={() => onViewChange("analytics")}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
        currentView === "analytics"
          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      }`}
    >
      <BarChart3 className="h-4 w-4" />
      <span className="hidden sm:inline">Statistiques</span>
    </button>
  </div>
);

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = "blue",
}) => (
  <div
    className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-${color}-50 to-${color}-100 dark:from-${color}-900/20 dark:to-${color}-800/20 p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg group`}
  >
    <div className="flex items-center justify-between mb-4">
      <div
        className={`p-3 rounded-xl bg-${color}-500/10 group-hover:bg-${color}-500/20 transition-colors duration-300`}
      >
        <Icon className={`h-6 w-6 text-${color}-600 dark:text-${color}-400`} />
      </div>
      {trend && (
        <div
          className={`flex items-center text-sm font-medium ${
            trend > 0 ? "text-green-600" : "text-red-600"
          }`}
        >
          <TrendingUp
            className={`h-4 w-4 mr-1 ${trend < 0 ? "rotate-180" : ""}`}
          />
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="space-y-1">
      <p className="text-2xl font-bold text-gray-900 dark:text-white">
        {value}
      </p>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {title}
      </p>
      {subtitle && (
        <p className="text-xs text-gray-500 dark:text-gray-500">{subtitle}</p>
      )}
    </div>
    <div
      className={`absolute -right-4 -bottom-4 w-24 h-24 bg-${color}-500/5 rounded-full blur-2xl`}
    ></div>
  </div>
);

import { GoogleGenerativeAI } from "@google/generative-ai";

const FormationCard = ({
  user,
  formation,
  onEnroll,
  onViewDetails,
  onEdit,
  onDelete,
  canManage,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "planifiee":
        return "bg-blue-500";
      case "en_cours":
        return "bg-green-500";
      case "terminee":
        return "bg-gray-500";
      case "annulee":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "interne":
        return <BookOpen className="h-4 w-4" />;
      case "externe":
        return <MapPin className="h-4 w-4" />;
      case "en_ligne":
        return <PlayCircle className="h-4 w-4" />;
      default:
        return <BookOpen className="h-4 w-4" />;
    }
  };

  const enrollmentPercentage =
    (formation._count?.inscriptions / formation.capacite) * 100;

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
      <div className="relative h-48 bg-gradient-to-br from-purple-400 via-pink-400 to-red-400 overflow-hidden">
        {!formation.thumbnail && (
          <img
            src={
              "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop"
            }
            alt={formation.titre}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300"></div>

        <div className="absolute top-4 left-4">
          <div
            className={`${getStatusColor(
              formation.statut
            )} px-3 py-1 rounded-full text-white text-xs font-medium flex items-center gap-1`}
          >
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            {formation.statut === "planifiee"
              ? "Planifiée"
              : formation.statut === "en_cours"
              ? "En Cours"
              : formation.statut === "terminee"
              ? "Terminée"
              : "Annulée"}
          </div>
        </div>

        <div className="absolute top-4 right-4">
          <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs font-medium flex items-center gap-1">
            {getTypeIcon(formation.type_formation)}
            {formation.type_formation === "interne"
              ? "Interne"
              : formation.type_formation === "externe"
              ? "Externe"
              : "En Ligne"}
          </div>
        </div>

        {formation.rating && (
          <div className="absolute bottom-4 left-4">
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
              <Star className="h-3 w-3 text-yellow-400 fill-current" />
              <span className="text-white text-xs font-medium">
                {formation.rating}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
            {formation.titre}
          </h3>
          <div className="flex gap-1 ml-2">
            <button
              onClick={() => onViewDetails(formation)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-200"
            >
              <Eye className="h-4 w-4" />
            </button>
            {canManage && (
              <>
                <button
                  onClick={() => onEdit(formation)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete(formation)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/50 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {formation.description && (
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
            {formation.description}
          </p>
        )}

        {formation.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {formation.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
          {formation.date_debut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-500" />
              <span>
                {new Date(formation.date_debut).toLocaleDateString("fr-FR")}
              </span>
            </div>
          )}
          {formation.duree && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span>{formation.duree}h</span>
            </div>
          )}
          {formation.formateur && (
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-green-500" />
              <span>{formation.formateur}</span>
            </div>
          )}
          {formation.niveau && (
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-yellow-500" />
              <span>{formation.niveau}</span>
            </div>
          )}
        </div>

        {formation.capacite && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Inscriptions
              </span>
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {formation._count?.inscriptions || 0}/{formation.capacite}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  enrollmentPercentage >= 80
                    ? "bg-red-500"
                    : enrollmentPercentage >= 60
                    ? "bg-yellow-500"
                    : "bg-green-500"
                }`}
                style={{ width: `${enrollmentPercentage}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          {formation.prix && (
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {new Intl.NumberFormat("fr-MA", {
                style: "currency",
                currency: "MAD",
              }).format(formation.prix)}
            </div>
          )}

          {formation.statut === "planifiee" &&
            (() => {
              const isAlreadyEnrolled = formation.inscriptions?.some(
                (inscription) => inscription.employe?.id === user?.id
              );
              const isFormationFull =
                formation.capacite &&
                formation._count?.inscriptions >= formation.capacite;

              if (isAlreadyEnrolled) {
                return (
                  <button
                    disabled
                    className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm bg-gray-100 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                  >
                    <UserPlus className="h-4 w-4" />
                    Déjà Inscrit
                  </button>
                );
              }

              return (
                <button
                  onClick={() => onEnroll(formation.id)}
                  disabled={isFormationFull}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 ${
                    isFormationFull
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white hover:shadow-lg hover:scale-105"
                  }`}
                >
                  <UserPlus className="h-4 w-4" />
                  {isFormationFull ? "Complet" : "S'inscrire"}
                </button>
              );
            })()}
        </div>
      </div>
    </div>
  );
};

const ListView = ({
  formations,
  onEnroll,
  onViewDetails,
  onEdit,
  onDelete,
  canManage,
  user,
}) => (
  <div className="space-y-4">
    {formations.map((formation) => (
      <div
        key={formation.id}
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-all duration-200"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {formation.titre}
              </h3>
              <div className="flex gap-2">
                <Badge
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    formation.statut === "planifiee"
                      ? "bg-blue-100 text-blue-700"
                      : formation.statut === "en_cours"
                      ? "bg-green-100 text-green-700"
                      : formation.statut === "terminee"
                      ? "bg-gray-100 text-gray-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {formation.statut === "planifiee"
                    ? "Planifiée"
                    : formation.statut === "en_cours"
                    ? "En Cours"
                    : formation.statut === "terminee"
                    ? "Terminée"
                    : "Annulée"}
                </Badge>
                <Badge className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  {formation.type_formation === "interne"
                    ? "Interne"
                    : formation.type_formation === "externe"
                    ? "Externe"
                    : "En Ligne"}
                </Badge>
              </div>
            </div>

            {formation.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {formation.description}
              </p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
              {formation.date_debut && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Calendar className="h-4 w-4" />
                  {new Date(formation.date_debut).toLocaleDateString("fr-FR")}
                </div>
              )}
              {formation.duree && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Clock className="h-4 w-4" />
                  {formation.duree}h
                </div>
              )}
              {formation.capacite && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Users className="h-4 w-4" />
                  {formation._count?.inscriptions || 0}/{formation.capacite}
                </div>
              )}
              {formation.rating && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Star className="h-4 w-4" />
                  {formation.rating}
                </div>
              )}
              {formation.formateur && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <GraduationCap className="h-4 w-4" />
                  {formation.formateur}
                </div>
              )}
              {formation.prix && (
                <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                  {new Intl.NumberFormat("fr-MA", {
                    style: "currency",
                    currency: "MAD",
                  }).format(formation.prix)}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-6">
            {formation.statut === "planifiee" &&
              (() => {
                const isAlreadyEnrolled = formation.inscriptions?.some(
                  (inscription) => inscription.employe?.id === user?.id
                );
                const isFormationFull =
                  formation.capacite &&
                  formation._count?.inscriptions >= formation.capacite;

                if (isAlreadyEnrolled) {
                  return (
                    <Button size="sm" variant="outline" disabled>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Déjà Inscrit
                    </Button>
                  );
                }

                return (
                  <Button
                    size="sm"
                    onClick={() => onEnroll(formation.id)}
                    variant="outline"
                    disabled={isFormationFull}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {isFormationFull ? "Complet" : "S'inscrire"}
                  </Button>
                );
              })()}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onViewDetails(formation)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Voir les Détails
                </DropdownMenuItem>
                {canManage && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onEdit(formation)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(formation)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const AnalyticsView = ({ stats }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total des Formations"
        value={stats?.totalFormations || 0}
        subtitle={`${stats?.activeFormations || 0} actives`}
        icon={GraduationCap}
        trend={12}
        color="purple"
      />
      <StatCard
        title="Inscriptions"
        value={stats?.totalInscriptions || 0}
        subtitle={`${stats?.completedInscriptions || 0} terminées`}
        icon={Users}
        trend={8}
        color="blue"
      />
      <StatCard
        title="Taux de Complétion"
        value={`${stats?.completionRate || 0}%`}
        subtitle="Formations terminées"
        icon={Trophy}
        trend={5}
        color="green"
      />
      <StatCard
        title="Budget Total"
        value={new Intl.NumberFormat("fr-MA", {
          style: "currency",
          currency: "MAD",
          notation: "compact",
        }).format(stats?.totalBudget || 0)}
        subtitle="Investissement en formation"
        icon={TrendingUp}
        trend={15}
        color="orange"
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-indigo-100 dark:border-indigo-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Target className="h-5 w-5 text-indigo-600" />
          Objectifs de Formation
        </h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Formations terminées ce mois
            </span>
            <span className="font-semibold text-gray-900 dark:text-white">
              85%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-indigo-500 h-2 rounded-full"
              style={{ width: "85%" }}
            ></div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-green-100 dark:border-green-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5 text-green-600" />
          Performance
        </h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Satisfaction moyenne
            </span>
            <span className="font-semibold text-gray-900 dark:text-white">
              4.7/5
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: "94%" }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function ModernTrainingDashboard() {
  const { toast } = useToast();
  const { user } = useAuth();

  const [currentView, setCurrentView] = useState("grid");
  const [formations, setFormations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const currentPage = pagination.current_page;
  const totalPages = pagination.total_pages;
  const setCurrentPage = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  const canManageTraining = user?.role === "admin" || user?.role === "rh";

  useEffect(() => {
    fetchFormations();
    fetchStats();
  }, [searchTerm, statusFilter, typeFilter, currentPage]);

  const fetchFormations = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        type_formation: typeFilter !== "all" ? typeFilter : undefined,
      };

      const response = await trainingAPI.getAll(params);
      setFormations(response.data.formations);
      setPagination(response.data.pagination);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les formations",
        variant: "destructive",
      });
      console.error("Erreur lors du chargement des formations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await trainingAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques:", error);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await trainingAPI.create(formData);
      toast({
        title: "Succès",
        description: "Formation créée avec succès",
      });
      setShowCreateModal(false);
      fetchFormations();
      fetchStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de la création",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (formData) => {
    try {
      await trainingAPI.update(selectedFormation.id, formData);
      toast({
        title: "Succès",
        description: "Formation mise à jour avec succès",
      });
      setShowEditModal(false);
      setSelectedFormation(null);
      fetchFormations();
      fetchStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de la mise à jour",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await trainingAPI.delete(selectedFormation.id);
      toast({
        title: "Succès",
        description: "Formation supprimée avec succès",
      });
      setShowDeleteModal(false);
      setSelectedFormation(null);
      fetchFormations();
      fetchStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de la suppression",
        variant: "destructive",
      });
    }
  };

  const handleEnroll = async (formationId) => {
    try {
      await trainingAPI.enroll(formationId, {});
      toast({
        title: "Succès",
        description: "Inscription réussie",
      });
      fetchFormations();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de l'inscription",
        variant: "destructive",
      });
    }
  };

  const handleViewDetails = (formation) => {
    setSelectedFormation(formation);
    setShowDetailsModal(true);
  };

  const handleEditClick = (formation) => {
    setSelectedFormation(formation);
    setShowEditModal(true);
  };

  const handleDeleteClick = (formation) => {
    setSelectedFormation(formation);
    setShowDeleteModal(true);
  };

  const filteredFormations = formations.filter((formation) => {
    const matchesSearch =
      formation.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (formation.description &&
        formation.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus =
      statusFilter === "all" || formation.statut === statusFilter;
    const matchesType =
      typeFilter === "all" || formation.type_formation === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="mx-auto p-6 space-y-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Formation et Développement
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Explorez, gérez et suivez vos programmes de formation
            </p>
          </div>

          <div className="flex items-center gap-4">
            <ViewToggle
              currentView={currentView}
              onViewChange={setCurrentView}
            />
            {canManageTraining && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:shadow-lg hover:scale-105"
              >
                <Plus className="h-5 w-5" />
                Nouvelle Formation
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
          <Button className="flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200">
            <Download className="h-4 w-4" />
            Exporter
          </Button>
          <Button className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors duration-200">
            <Share2 className="h-4 w-4" />
            Partager
          </Button>
          <Button className="flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors duration-200">
            <Bell className="h-4 w-4" />
            Notifications
          </Button>
          <Button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200">
            <Settings className="h-4 w-4" />
            Paramètres
          </Button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Rechercher une formation, un formateur ou un sujet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les Statuts</SelectItem>
                  <SelectItem value="planifiee">Planifiée</SelectItem>
                  <SelectItem value="en_cours">En Cours</SelectItem>
                  <SelectItem value="terminee">Terminée</SelectItem>
                  <SelectItem value="annulee">Annulée</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les Types</SelectItem>
                  <SelectItem value="interne">Interne</SelectItem>
                  <SelectItem value="externe">Externe</SelectItem>
                  <SelectItem value="en_ligne">En Ligne</SelectItem>
                </SelectContent>
              </Select>

              <Button className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-xl hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-all duration-200 font-medium min-w-[120px]">
                <Filter className="h-4 w-4" />
                Filtres
              </Button>
            </div>
          </div>
        </div>

        <div className="transition-all duration-500 ease-in-out">
          {currentView === "analytics" && <AnalyticsView stats={stats} />}

          {currentView === "grid" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {filteredFormations.length} formation
                  {filteredFormations.length !== 1 ? "s" : ""} trouvée
                  {filteredFormations.length !== 1 ? "s" : ""}
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <CalendarIcon className="h-4 w-4" />
                  Dernière mise à jour : il y a 2 minutes
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <GraduationCap className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              ) : filteredFormations.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <div className="mx-auto w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center mb-6">
                    <GraduationCap className="h-12 w-12 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Aucune formation trouvée
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    {searchTerm ||
                    statusFilter !== "all" ||
                    typeFilter !== "all"
                      ? "Aucune formation ne correspond à vos critères de recherche. Essayez de modifier vos filtres."
                      : canManageTraining
                      ? "Commencez par créer votre première formation pour développer les compétences de votre équipe."
                      : "Aucune formation disponible pour le moment. Revenez bientôt !"}
                  </p>
                  {canManageTraining && (
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:shadow-lg hover:scale-105"
                    >
                      <Plus className="h-5 w-5" />
                      Créer une Formation
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredFormations.map((formation) => (
                    <FormationCard
                      user={user}
                      key={formation.id}
                      formation={formation}
                      onEnroll={handleEnroll}
                      onViewDetails={handleViewDetails}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteClick}
                      canManage={canManageTraining}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {currentView === "list" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Vue Liste - {filteredFormations.length} formation
                  {filteredFormations.length !== 1 ? "s" : ""}
                </h2>
              </div>

              <ListView
                formations={filteredFormations}
                onEnroll={handleEnroll}
                onViewDetails={handleViewDetails}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                canManage={canManageTraining}
                user={user}
              />
            </div>
          )}
        </div>

        {!loading && totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      currentPage > 1 && setCurrentPage(currentPage - 1)
                    }
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => setCurrentPage(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      currentPage < totalPages &&
                      setCurrentPage(currentPage + 1)
                    }
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        {canManageTraining && (
          <div className="fixed bottom-8 right-8">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="group relative bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-full shadow-2xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 hover:scale-110 hover:shadow-purple-500/25"
            >
              <Plus className="h-6 w-6" />
              <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-3 opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap">
                <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium">
                  Créer une Formation
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
                </div>
              </div>
            </Button>
          </div>
        )}

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-2xl p-6 border border-purple-100 dark:border-purple-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                <Trophy className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Objectif Mensuel : 85% de complétion
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Vous êtes sur la bonne voie ! 78% atteint ce mois.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats?.activeFormations || 0}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  En Cours
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats?.completedInscriptions || 0}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Terminées
                </div>
              </div>
              <Button className="flex items-center gap-2 bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 px-4 py-2 rounded-xl font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-200">
                Voir le Rapport Complet
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {showCreateModal && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer une Nouvelle Formation</DialogTitle>
                <DialogDescription>
                  Remplissez les détails pour créer un nouveau programme de
                  formation
                </DialogDescription>
              </DialogHeader>
              <TrainingForm
                onSubmit={handleCreate}
                onCancel={() => setShowCreateModal(false)}
              />
            </DialogContent>
          </Dialog>
        )}

        {showEditModal && selectedFormation && (
          <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Modifier la Formation</DialogTitle>
                <DialogDescription>
                  Modifiez les détails de la formation
                </DialogDescription>
              </DialogHeader>
              <TrainingForm
                initialData={selectedFormation}
                onSubmit={handleEdit}
                onCancel={() => {
                  setShowEditModal(false);
                  setSelectedFormation(null);
                }}
              />
            </DialogContent>
          </Dialog>
        )}

        {showDetailsModal && selectedFormation && (
          <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Détails de la Formation</DialogTitle>
              </DialogHeader>
              <TrainingDetails
                formation={selectedFormation}
                onClose={() => {
                  setShowDetailsModal(false);
                  setSelectedFormation(null);
                }}
              />
            </DialogContent>
          </Dialog>
        )}

        {showDeleteModal && selectedFormation && (
          <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmer la Suppression</DialogTitle>
                <DialogDescription>
                  Êtes-vous sûr de vouloir supprimer la formation "
                  {selectedFormation.titre}" ? Cette action est irréversible.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedFormation(null);
                  }}
                >
                  Annuler
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Supprimer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
